import { mediaUrl } from '../../lib/media-url.js';
import { useState } from 'react';
import { ShoppingCart, Trash2, ArrowLeft, Package, ShieldCheck, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { QuoteRequestForm } from '../inquiry/QuoteRequestForm.jsx';
import { useCart } from '../../store/cart.jsx';
import { hasUnknownPriceItems } from '../../store/cart-state.js';
import { formatCurrency } from '../../lib/utils.js';
import { computeGrossTotals, displayUnitCents, priceDisplayMode, vatRateOf } from '../../lib/price.js';
import { useTenant } from '../../context/TenantContext.jsx';
import { isStripeCheckout } from '../../lib/checkout-mode.js';

export function CartView({ onNavigate, onSuccess }) {
  const { items, totalItems, updateQuantity, removeItem, clearCart } = useCart();
  const [submittingOci, setSubmittingOci] = useState(false);
  const [ociError, setOciError] = useState(null);
  const { tenant } = useTenant();
  const stripeCheckout = isStripeCheckout(tenant);
  // a-055b: Modus je Ansicht. Kauf = brutto (= Stripe-Betrag). Anfrage und OCI = netto (= ES2000-Übergabe).
  const priceMode = priceDisplayMode(tenant);
  const vatRate = vatRateOf(tenant);
  const totals = computeGrossTotals(items, vatRate);
  const shownTotalCents = priceMode === 'gross' ? totals.grossCents : totals.netCents;
  const cartLabel = stripeCheckout ? 'Warenkorb' : 'Anfragekorb';

  const searchParams = typeof window !== 'undefined' ? new URL(window.location.href).searchParams : new URLSearchParams();
  const isOciMode = searchParams.get('mode') === 'oci';

  const handleOciCheckout = async () => {
    if (!items.length || submittingOci) return;
    setSubmittingOci(true);
    setOciError(null);

    try {
      const response = await fetch('/api/oci/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            sku: item.sku || item.productId || item.id,
            quantity: item.quantity,
          })),
        }),
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({ error: 'Übertragung an ES2000 fehlgeschlagen.' }));
        throw new Error(errJson.error || `HTTP ${response.status}: Übertragung fehlgeschlagen`);
      }

      const html = await response.text();

      // Auto-submit the received OCI form into the document
      document.open();
      document.write(html);
      document.close();
    } catch (err) {
      setOciError(err.message || 'Fehler bei der OCI-Übergabe an ES2000.');
      setSubmittingOci(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-16 text-center space-y-6">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--surface-container-low)] text-[var(--secondary)] mx-auto">
          <ShoppingCart className="h-10 w-10" />
        </div>
        <div className="space-y-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[var(--foreground)]">
            {stripeCheckout ? 'Ihr Warenkorb ist leer' : 'Ihr Anfragekorb ist leer'}
          </h1>
          <p className="text-sm text-[var(--secondary)] max-w-md mx-auto">
            {stripeCheckout
              ? 'Sie haben noch keine Artikel in den Warenkorb gelegt.'
              : 'Sie haben noch keine Artikel zur unverbindlichen Angebotsanfrage vorgemerkt.'}
          </p>
        </div>
        <Button
          size="lg"
          onClick={() => onNavigate(isOciMode ? '/?mode=oci' : '/catalog')}
          className="gap-2 font-bold cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Katalog durchsuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 space-y-8">
      
      {/* OCI Mode Status Banner */}
      {isOciMode && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="oci-status-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-600 text-white flex items-center justify-center font-extrabold text-sm shrink-0">
              OCI
            </div>
            <div>
              <h3 className="text-sm font-bold text-sky-950">OCI-Katalogmodus: Verbunden mit ES2000 (Pilot)</h3>
              <p className="text-xs text-sky-800">
                Ihre Artikelauswahl wird per PunchOut direkt an Ihre Kalkulationsmappe übergeben.
                <span className="ml-2 inline-flex items-center text-xs font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                  Transport-PoC (Shoppreise)
                </span>
              </p>
            </div>
          </div>
          <div className="text-xs font-mono text-sky-700 bg-sky-100/80 px-3 py-1.5 rounded-lg shrink-0">
            Sitzung: Aktiv
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[var(--border)] pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-[var(--secondary)] mb-1">
            <button onClick={() => onNavigate(isOciMode ? '/?mode=oci' : '/')} className="hover:text-[var(--primary)] cursor-pointer">Startseite</button>
            <span>/</span>
            <span className="text-[var(--foreground)] font-bold">{isOciMode ? 'OCI-Warenkorb' : cartLabel}</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)]">
            {isOciMode ? 'OCI-Warenkorb (ES2000 PunchOut)' : (stripeCheckout ? 'Warenkorb & Kasse' : 'Anfragekorb & Projektanfrage')}
          </h1>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={clearCart}
          className="gap-1.5 text-xs text-[var(--secondary)] hover:text-red-600 cursor-pointer self-start sm:self-auto"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Warenkorb leeren
        </Button>
      </div>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_440px]">
        
        {/* Left Column: Items Table */}
        <div className="min-w-0 space-y-6">
          <div className="rounded-2xl border border-[var(--border)] bg-white overflow-hidden shadow-sm">
            <div className="p-4 bg-[var(--surface-container-low)] border-b border-[var(--border)] flex justify-between items-center text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
              <span>Ausgewählte Artikel ({totalItems})</span>
              <span className="hidden sm:inline">Positionen</span>
            </div>

            <div className="divide-y divide-[var(--border)]">
              {items.map((item) => {
                const sku = item.sku || item.productId || item.id;
                const unitPrice = item.unitPriceCents || 0;
                const shownUnit = displayUnitCents(unitPrice, priceMode, vatRate);
                const lineTotal = shownUnit * item.quantity;

                return (
                  <div key={sku} className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    
                    {/* Item Image & Info */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl border border-[var(--border)] bg-[var(--surface-container-low)] p-2 flex items-center justify-center shrink-0">
                        {item.primaryImageUrl ? (
                          <img src={mediaUrl(item.primaryImageUrl)} alt="" className="h-full w-full object-contain" />
                        ) : (
                          <Package className="h-8 w-8 text-[var(--secondary)]/40" />
                        )}
                      </div>

                      <div className="min-w-0 space-y-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-[var(--primary)]">
                          {item.manufacturer || 'Fachhandel'}
                        </span>
                        <h4 className="text-sm font-bold text-[var(--foreground)] leading-snug truncate">
                          {item.name}
                        </h4>
                        <p className="text-xs font-mono text-[var(--secondary)]">
                          Art.-Nr. {item.sku}
                        </p>
                        <p className="text-xs font-medium text-[var(--foreground)] sm:hidden">
                          Einzelpreis: {unitPrice > 0 ? formatCurrency(shownUnit) : 'Auf Anfrage'}
                        </p>
                      </div>
                    </div>

                    {/* Quantity Selector & Price */}
                    <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-[var(--border)]">
                      
                      {/* Quantity Selector */}
                      <div className="flex items-center rounded-[var(--radius)] border border-[var(--border)] bg-white text-xs">
                        <button
                          onClick={() => updateQuantity(sku, item.quantity - 1)}
                          className="px-3 py-1.5 font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                          aria-label="Menge verringern"
                        >
                          -
                        </button>
                        <span className="px-3 py-1.5 font-bold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(sku, item.quantity + 1)}
                          className="px-3 py-1.5 font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                          aria-label="Menge erhöhen"
                        >
                          +
                        </button>
                      </div>

                      {/* Line Total */}
                      <div className="text-right min-w-[90px]">
                        <p className="text-sm font-extrabold text-[var(--foreground)]">
                          {unitPrice > 0 ? formatCurrency(lineTotal) : 'Auf Anfrage'}
                        </p>
                        {unitPrice > 0 && item.quantity > 1 && (
                          <p className="text-xs text-[var(--secondary)]">
                            {item.quantity} × {formatCurrency(shownUnit)}
                          </p>
                        )}
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeItem(sku)}
                        className="text-[var(--secondary)] hover:text-red-600 p-1.5 transition-colors cursor-pointer"
                        title="Position entfernen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>

                    </div>

                  </div>
                );
              })}
            </div>

            {/* Subtotal Summary Footer */}
            <div className="p-4 sm:p-6 bg-[var(--surface-container-low)] border-t border-[var(--border)] flex justify-between items-center text-sm">
              <span className="font-bold text-[var(--foreground)]">{priceMode === 'gross' ? `Gesamtsumme (${totalItems} Positionen, inkl. MwSt.):` : `Zwischensumme (${totalItems} Positionen, netto):`}</span>
              <span className="font-display text-xl font-extrabold text-[var(--foreground)]">
                {hasUnknownPriceItems(items) ? 'Auf Anfrage' : formatCurrency(shownTotalCents)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-[var(--secondary)] bg-[var(--surface-container-low)] p-4 rounded-2xl border border-[var(--border)]">
            <ShieldCheck className="h-5 w-5 text-[var(--primary)] shrink-0" />
            <p>
              {isOciMode
                ? 'Übertragen werden die aktuell im Shop verfügbaren Preise. Kundenspezifische ASL-Konditionen sind in diesem PoC noch nicht angebunden.'
                : stripeCheckout
                  ? 'Alle Preise verstehen sich inkl. gesetzlicher Mehrwertsteuer. Die Zahlung erfolgt sicher über Stripe.'
                  : 'Alle angegebenen Preise verstehen sich zzgl. gesetzlicher Mehrwertsteuer. Ihr Anfragekorb ist noch keine Bestellung und wird noch nicht bezahlt: Nach dem Absenden erhalten Sie von uns eine persönliche Rückmeldung mit einem verbindlichen Angebot.'}
            </p>
          </div>
        </div>

        {/* Right Column: Submission Form / OCI PunchOut Trigger */}
        <div>
          {isOciMode ? (
            <div className="rounded-2xl border border-[var(--border)] bg-white p-6 sm:p-8 space-y-6 shadow-sm sticky top-24" data-testid="oci-checkout-card">
              <div className="space-y-2 border-b border-[var(--border)] pb-4">
                <h3 className="font-display text-xl font-bold text-[var(--foreground)]">
                  ES2000 PunchOut-Abschluss
                </h3>
                <p className="text-xs text-[var(--secondary)]">
                  Übertragen Sie die {totalItems} Positionen direkt in Ihre ES2000-Kalkulationsmappe.
                </p>
              </div>

              {ociError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-600" />
                  <div>
                    <strong className="font-bold">Fehler bei der Übertragung:</strong>
                    <p className="mt-0.5">{ociError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-3 text-xs bg-[var(--surface-container-low)] p-4 rounded-xl border border-[var(--border)]">
                <div className="flex justify-between font-medium">
                  <span className="text-[var(--secondary)]">Positionen:</span>
                  <span className="font-bold text-[var(--foreground)]">{totalItems}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-[var(--secondary)]">Gesamtsumme (netto, zzgl. MwSt.):</span>
                  <span className="font-bold text-[var(--foreground)]">
                    {hasUnknownPriceItems(items) ? 'Auf Anfrage' : formatCurrency(totals.netCents)}
                  </span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-[var(--secondary)]">Protokoll:</span>
                  <span className="font-mono text-sky-700 font-bold">SAP OCI 5.0 / ES2000</span>
                </div>
              </div>

              <Button
                id="oci-checkout-submit"
                size="lg"
                onClick={handleOciCheckout}
                disabled={submittingOci || items.length === 0}
                className="w-full gap-2 font-bold bg-sky-600 hover:bg-sky-700 text-white shadow-md cursor-pointer py-6 text-base"
              >
                {submittingOci ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Wird an ES2000 übergeben...
                  </>
                ) : (
                  <>
                    An ES2000 übergeben
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-[var(--secondary)]">
                Nach Klick auf den Button wird das Formular automatisch an Ihre ES2000-Rücksende-URL übermittelt.
              </p>
            </div>
          ) : (
            <QuoteRequestForm
              onSuccess={onSuccess}
              onNavigate={onNavigate}
              stripeCheckout={stripeCheckout}
            />
          )}
        </div>

      </div>

    </div>
  );
}
